import { config } from '@/config/env'
import type { Game } from './types'

/**
 * Release manifests: a small JSON file per game, published next to its builds,
 * that names the active version. For example
 * https://games.zarcade.io/monkey-business/current.json:
 *
 *   { "gameId": "monkey-business", "version": "a96131fdb8b3",
 *     "url": "https://games.zarcade.io/monkey-business/versions/a96131fdb8b3/index.html", ... }
 *
 * The manifest is external data and decides what page runs in the player, so
 * it is validated strictly: the URL must be an HTTPS page on the trusted games
 * origin, inside that game's own folder.
 */

export interface ReleaseBuild {
  url: string
  /** Null for builds without a known version (e.g. a developer override). */
  version: string | null
}

export class ManifestError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ManifestError'
  }
}

const VERSION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/
const FETCH_TIMEOUT_MS = 15_000

/** Resolves a catalog manifest location; it must be an HTTPS URL on the games origin. */
export function resolveManifestUrl(raw: string): URL | null {
  try {
    const url = new URL(raw, config.gamesBaseUrl ?? config.gamesOrigin)
    return isTrusted(url) ? url : null
  } catch {
    return null
  }
}

function isTrusted(url: URL): boolean {
  return url.protocol === 'https:' && url.origin === config.gamesOrigin && !url.username && !url.password
}

export function parseReleaseManifest(data: unknown, gameId: string): ReleaseBuild {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    throw new ManifestError('The release manifest is not a JSON object.')
  }
  const manifest = data as Record<string, unknown>

  if (manifest.gameId !== gameId) throw new ManifestError(`The release manifest is not for "${gameId}".`)

  const { version, url: rawUrl } = manifest
  if (typeof version !== 'string' || !VERSION_PATTERN.test(version)) {
    throw new ManifestError('The release manifest has no valid version.')
  }
  if (typeof rawUrl !== 'string') throw new ManifestError('The release manifest has no game URL.')

  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    throw new ManifestError('The release manifest has an invalid game URL.')
  }
  if (!isTrusted(url) || !url.pathname.startsWith(`/${gameId}/`) || !url.pathname.endsWith('.html')) {
    throw new ManifestError(`The release manifest points outside ${config.gamesOrigin}/${gameId}/.`)
  }
  return { url: url.href, version }
}

/**
 * Fetches the manifest, bypassing any cached copy, so each new play session
 * picks up the latest published version.
 */
export async function fetchReleaseBuild(game: Game, manifestUrl: URL, signal: AbortSignal): Promise<ReleaseBuild> {
  let response: Response
  try {
    response = await fetch(requestUrl(manifestUrl), {
      cache: 'no-cache',
      credentials: 'omit',
      redirect: 'error',
      signal: AbortSignal.any([signal, AbortSignal.timeout(FETCH_TIMEOUT_MS)]),
    })
  } catch (error) {
    if (signal.aborted) throw error
    throw new ManifestError(`Couldn’t reach the game server. Check your connection and try again.`)
  }
  if (!response.ok) throw new ManifestError(`The game server answered ${response.status}. Please try again later.`)

  let data: unknown
  try {
    data = await response.json()
  } catch {
    throw new ManifestError('The release manifest is not valid JSON.')
  }
  return parseReleaseManifest(data, game.id)
}

/**
 * The games domain only allows cross-origin reads from the live site, so the
 * dev server proxies it under /__games (see vite.config.ts). Production builds
 * always fetch the real URL.
 */
function requestUrl(url: URL): string {
  return import.meta.env.DEV ? `/__games${url.pathname}${url.search}` : url.href
}
